import polyline from "@mapbox/polyline";
import moment from "moment";
import { curvedOrStraightLine } from "./curveLine";
import { BUCKET_ICONS, resolvePlaceIcon } from "./placeTypes/resolvePlaceType";

export const getItemID = (item) =>
	item?.externalId ||
	item?.hotel_id ||
	item?.fsq_id ||
	item?.productCode ||
	item?.tour_id ||
	item?.alpha3 ||
	item?.id ||
	item?._id ||
	item?.googlePlaceId ||
	item?.normalised_city ||
	item?.city ||
	item?.country ||
	item?.type ||
	item?.__typename;
export const getItemType = (item) =>
	item?.hotel_id || item?.type === "accommodation"
		? "accommodation"
		: item?.fsq_id || item?.id?.includes?.("poi") || item?.type === "activity"
			? "activity"
			: item?.type === "cuisine"
				? "cuisine"
				: item?.segments
					? "transport"
					: item?.type === "city"
						? "city"
						: "tour";

export const getItemColor = (type) =>
	type === "activity" || type === "tour"
		? "primary-3"
		: type === "cuisine"
			? "primary-4"
			: type === "accommodation"
				? "primary-1"
				: type === "transport"
					? "primary-2"
					: "black";

export const getItemColorName = (type) =>
	type === "activity" || type === "tour"
		? "orange"
		: type === "cuisine"
			? "purple"
			: type === "accommodation"
				? "green"
				: type === "transport"
					? "magenta"
					: "black";

export const getItemColorCode = (type) => {
	const getColor = (varName: string, fallback: string) => getComputedStyle(document.documentElement).getPropertyValue(varName).trim() || fallback;

	return type === "activity" || type === "tour"
		? getColor("--main3", "#FFC14A")
		: type === "cuisine"
			? getColor("--main4", "#9376E8")
			: type === "accommodation"
				? getColor("--main1", "#13C892")
				: type === "transport"
					? getColor("--main2", "#EA5681")
					: "#0F3E4A";
};

export const getPartnerColor = (type) =>
	type === "booking"
		? "#0C3B7C"
		: type === "hostelworld"
			? "#F37349"
			: type === "airbnb"
				? "#FF385D"
				: type === "viator"
					? "#186B6D"
					: type === "getyourguide"
						? "#FF5533"
						: type === "tripadvisor"
							? "#34E0A1"
							: "#0F3E4A";

export const getUserType = (value) => {
	return value ? "creator" : "user";
};

export const getTrackingLabelForType = (type) => {
	return type === "accommodation" ? "sleep" : type === "activity" ? "see_do" : type === "cuisine" ? "eat_drink" : "article";
};

// 86cb7hz6z — the per-bucket fallback, and the ONLY one. There were three hand-written copies of
// this switch disagreeing with each other (utils/getters, components/common/Colors,
// pdfexport/elements/Colors): the same bucket rendered `see&do` in one component and
// `todo--outlined` in another, so which pin a place got depended on which surface drew it.
//
// The buckets now come from BUCKET_ICONS, the resolver's own floor, so this cannot drift from what
// getPlaceIcon falls back to. `transport` moved from "transport--outlined" to BUCKET_ICONS' `car`
// as part of that: the 675-row icon map emits 256 filled icons and exactly one outlined, so an
// outlined fallback sat visibly apart from every specific icon it appeared next to.
//
// `tour` and `city` are not PlaceBuckets and stay local, as does the `article` default.
export const getItemIcon = (type): any => BUCKET_ICONS[type] ?? (type === "tour" ? "tour" : type === "city" ? "marker" : "article");

const toFiniteCoordinate = (value: unknown): number | null => {
	if (value == null || value === "") return null;
	const parsed = typeof value === "number" ? value : Number(value);
	return Number.isFinite(parsed) ? parsed : null;
};

export const getGeocodes = (item): [number, number] | null => {
	if (!item) return null;

	let lng: number | null = null;
	let lat: number | null = null;

	if (item?.fsq_id) {
		lng = toFiniteCoordinate(item?.geocodes?.main?.longitude);
		lat = toFiniteCoordinate(item?.geocodes?.main?.latitude);
	} else if (item?.metadata && (item?.lng != null || item?.lat != null)) {
		lng = toFiniteCoordinate(item?.lng ?? item?.longitude);
		lat = toFiniteCoordinate(item?.lat ?? item?.latitude);
	} else if (item?.location && (item.location?.lng != null || item.location?.lat != null)) {
		lng = toFiniteCoordinate(item.location?.lng ?? item?.lng ?? item?.longitude);
		lat = toFiniteCoordinate(item.location?.lat ?? item?.lat ?? item?.latitude);
	} else {
		lng = toFiniteCoordinate(item?.lng ?? item?.longitude);
		lat = toFiniteCoordinate(item?.lat ?? item?.latitude);
	}

	if (lng === null || lat === null) return null;
	if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;

	return [lng, lat];
};

export const itemHasValidMapCoordinates = (item: unknown): boolean => getGeocodes(item) !== null;

const TRAVEL_MODE_KEYS: Record<string, string> = {
	plane: "transport.mode.plane",
	car: "transport.mode.car",
	rentalcar: "transport.mode.rentalcar",
	train: "transport.mode.train",
	bus: "transport.mode.bus",
	ferry: "transport.mode.ferry",
};

export const getTravelModeLabel = (value, t?: (key: string) => string) => {
	const normalised = typeof value === "string" ? value.toLowerCase() : value;
	const key = TRAVEL_MODE_KEYS[normalised];
	if (key && t) return t(key);
	// Fallback: hardcoded English (kept for callers without a translator)
	return value === "plane"
		? "Plane"
		: value === "car"
			? "Car"
			: value === "rentalcar"
				? "Rental car"
				: value === "train"
					? "Train"
					: value === "bus"
						? "Bus"
						: value === "ferry"
							? "Ferry"
							: "";
};

export const formatTransportDuration = (minutes: number | undefined | null, t: (key: string, opts?: Record<string, unknown>) => string): string => {
	if (!minutes || minutes <= 0) return "";
	const total = Math.round(minutes);
	const hours = Math.floor(total / 60);
	const mins = total - hours * 60;
	if (hours > 0 && mins > 0) return t("transport.duration.hoursMinutes", { hours, minutes: mins });
	if (hours > 0) return t("transport.duration.hoursOnly", { hours });
	return t("transport.duration.minutesOnly", { minutes: mins });
};

export const getTransports = (destinations, transports) => {
	const bookedPaths = [];
	const nonBookedPaths = [];
	// Ticket 5 (transport-lines.md "Curved lines"): each leg bends alternately left/right so a
	// multi-leg trip reads as a route rather than a zigzag — a single-leg trip (one segment total)
	// stays straight, since a curve there reads as an error rather than a route.
	const totalSegments = Math.max((destinations?.length ?? 0) - 1, 0);
	destinations?.map((destination, index) => {
		const next = destinations?.[index + 1];
		const fallbackLine = () => curvedOrStraightLine([destination?.longitude, destination.latitude], [next?.longitude, next?.latitude], index, totalSegments);
		return transports?.[destination._id]?.destinationTo === next?._id && transports?.[destination._id]?.segments?.length > 0
			? bookedPaths.push(
					//@ts-ignore
					transports?.[destination._id]?.segments?.map((segment) =>
						transports?.[destination._id]?.isStipplTransport && !!segment.path?.coordinates
							? segment.path
							: !transports?.[destination._id]?.isStipplTransport && !!segment.path
								? polyline.toGeoJSON(segment.path)
								: fallbackLine()
					)
				)
			: index < destinations?.length - 1
				? nonBookedPaths.push(fallbackLine())
				: null;
	});
	return { bookedPaths, nonBookedPaths };
};

export const getPlannedItems = (currentTrip) => {
	// Use destination.places instead of deprecated all* fields
	// Filter out places without valid coordinates
	const allPlaces =
		currentTrip.destinations?.flatMap?.((destination) => (destination?.places || []).filter((place) => place && itemHasValidMapCoordinates(place))) || [];

	return [...(currentTrip?.destinations || []).filter((destination) => itemHasValidMapCoordinates(destination)), ...allPlaces];
};

export const getRange = (startDate, endDate, type) => {
	let fromDate = moment(startDate);
	let toDate = moment(endDate);
	let diff = toDate.diff(fromDate, type);
	let range = [];
	for (let i = 0; i <= diff; i++) {
		range.push(moment(startDate).add(i, type));
	}
	return range;
};

// 123yb69126r / item 1 of 86cbbhm9h. The legacy category ObjectIds below encode FINE-GRAINED
// categories (Sights & Landmarks, Nature & Outdoors, Tours & Attractions, Culture & Activities,
// Sports & Wellness, Beaches & Lakes, Shopping & Souvenirs). AI-chat and Serper-sourced places
// carry none of those ObjectIds, so a place like a museum fell all the way through to the generic
// "activity" bucket sprite (seedoMarker, the orange camera QA saw) — while the card for the SAME
// place resolved a specific icon via the shared vocabulary (getPlaceIcon -> resolvePlaceIcon,
// utils/placeTypes/resolvePlaceType.ts). Card and pin disagreed.
//
// This table lets `getMapMarkerIcon` ask that same resolver for a specific sprite when there is no
// legacy category match, instead of only ever falling to the coarse bucket. It intentionally only
// covers icon names we are confident pair with one specific sprite below — anything else (including
// genuinely ambiguous ones like "swimming" or "festival", which could plausibly belong to more than
// one legacy bucket) falls through to the generic seedoMarker exactly as it does today. A
// wrong-but-specific icon is worse than the generic one QA complained about, so this stays small on
// purpose rather than trying to cover the full ~200-icon vocabulary.
const ACTIVITY_ICON_TO_MARKER: Record<string, string> = {
	// Sights & Landmarks
	landmark: "landmarksMarker",
	heritageSite: "landmarksMarker",
	museum: "landmarksMarker",
	historic: "landmarksMarker",
	church: "landmarksMarker",
	"city-church": "landmarksMarker",
	obelisk: "landmarksMarker",
	ruins: "landmarksMarker",
	bridge: "landmarksMarker",

	// Nature & Outdoors
	nature: "natureMarker",
	naturalFeature: "natureMarker",
	nationalPark: "natureMarker",
	mountainrange: "natureMarker",
	hike: "natureMarker",
	wildlife: "natureMarker",
	waterfall: "natureMarker",
	cave: "natureMarker",
	park: "natureMarker",
	island: "natureMarker",
	scenic: "natureMarker",
	trekkin: "natureMarker",
	walk: "natureMarker",
	playground: "natureMarker",
	fish: "natureMarker",

	// Tours & Attractions
	tour: "toursMarker",
	adventure: "toursMarker",

	// Culture & Activities
	culture: "cultureMarker",
	theatre: "cultureMarker",
	arts: "cultureMarker",

	// Sports & Wellness
	sports: "sportsMarker",
	wellness: "sportsMarker",
	gym: "sportsMarker",
	dumbbell: "sportsMarker",
	crossfit: "sportsMarker",

	// Beaches & Lakes
	beach: "beachMarker",

	// Shopping & Souvenirs
	shopping: "shoppingMarker",
	clothing: "shoppingMarker",
	supermarket: "shoppingMarker",
	gift: "shoppingMarker",
	makeup: "shoppingMarker",
	"market-square": "shoppingMarker",
	stall: "shoppingMarker",

	// ── 123yb69126r round 2 ────────────────────────────────────────────────────────────────────
	// Persisting the resolved icon on the saved Place (poiParser.ts) fixed places whose icon
	// happened to be in this table and changed nothing for the rest — a museum resolving to `bust`
	// or a temple to `guru` still fell through to the generic camera, so card and pin still
	// disagreed, which is the actual complaint.
	//
	// Measured rather than guessed: of the 154 distinct icons reachable by an activity-bucket place
	// (categories see&do / outdoors / shopping / practical in typeIconMap.json), only 42 had a row —
	// 144 of 339 source types. The rows below add the highest-traffic pairings where the legacy
	// bucket is unambiguous, taking it to ~213/339. The rule is unchanged: a confidently WRONG pin
	// is worse than the generic one, so anything that straddles two legacy buckets stays out and is
	// listed at the bottom with the reason.
	mosque: "landmarksMarker", // place of worship, mosque — same family as `church` above
	bust: "landmarksMarker", // sculpture, shrine, sculpture museum
	"taj-mahal": "landmarksMarker", // monastery, castle/palace
	guru: "landmarksMarker", // buddhist temple
	"edvard-munch": "landmarksMarker", // art museum, modern art museum — matches `museum` above

	"water-element": "natureMarker", // bay, water, fjord, lagoon, pond
	creek: "natureMarker", // river, canal
	"coniferous-tree": "natureMarker", // botanical garden
	landscape: "natureMarker", // land mass, terrain

	// Lakes belong to Beaches & Lakes, not Nature & Outdoors — the legacy bucket splits them.
	"sea-waves": "beachMarker", // lake, reservoir

	spa: "sportsMarker", // thermal baths, massage/day/health spa, sauna, public bath
	"outdoor-swimming-pool": "sportsMarker", // lido, public swimming pool
	"scuba-mask": "sportsMarker", // diving centre, dive school, scuba tour agency
	watersports: "sportsMarker", // water sports rental, water activities
	"golf-hole": "sportsMarker", // golf course, golf club
	"go-kart": "sportsMarker", // go-karting venue, car racing venue
	"hot-springs": "sportsMarker", // hot spring — wellness, consistent with `spa`
	stadium: "sportsMarker",

	"online-store": "shoppingMarker", // hobby / leather / general / electronics / collectibles store
	shop: "shoppingMarker", // traditional market, tattoo shop
	"small-business": "shoppingMarker", // shopping mall, store
	"feminine-perfume-bottle": "shoppingMarker", // cosmetics store, perfume store

	easel: "cultureMarker", // art gallery, exhibit, art centre
	"film-reel": "cultureMarker", // movie theatre — matches `theatre` above

	animation: "toursMarker", // theme park, amusement park — an attraction, not a "culture" venue

	// STILL EXCLUDED ON PURPOSE, because each straddles two legacy buckets and a wrong pin is worse:
	//   book-shelf   book store [shopping] vs public library [see&do]
	//   boat         boat tour agency [see&do] vs boat rental / cruise line [transport]
	//   swimming     water park + swimming lake [outdoors] vs swimming pool [see&do]
	//   entertainment  event venue / ticket seller / show — venue type is unknowable from the icon
	//   see&do       the generic bucket icon itself; adding it would defeat the whole fallback
};

// Resolves a specific marker sprite for an "activity" place that has no legacy category ObjectId,
// via the same shared resolver the card uses. Returns undefined (never a guess) when the place has
// nothing to resolve from, or when the resolved icon isn't one of the confident pairs above.
const resolveActivityMarkerSprite = (place): string | undefined => {
	if (!place) return undefined;

	// A specific stored icon (a user's manual pick, or an already-resolved value) wins over
	// re-deriving one from the type — same priority getPlaceIcon (utils/places.ts) gives the card.
	// Generic bucket icons (e.g. "see&do") are simply absent from the table above, so they fall
	// through to the type-based resolution below instead of matching here.
	if (place.icon && ACTIVITY_ICON_TO_MARKER[place.icon]) return ACTIVITY_ICON_TO_MARKER[place.icon];

	const type = place?.type || place?.attraction;
	if (!type) return undefined;

	const icon = resolvePlaceIcon({ source: "serper", type: String(type).toLowerCase(), category: place?.attraction });
	return ACTIVITY_ICON_TO_MARKER[icon];
};

export const getMapMarkerIcon = (cat, place?) => {
	switch (cat) {
		//Sights & Landmarks
		case "6030e3ee749499e147722a95":
			return "landmarksMarker";
		//Nature & Outdoors
		case "6030e3ee749499e147722a99":
			return "natureMarker";
		//Tours & Attractions
		case "6030e3ee749499e147722a96":
			return "toursMarker";
		//Culture & Activities
		case "637e116eddc2e1237e0f1d27":
			return "cultureMarker";
		//Sports & Wellness
		case "6030e3ee749499e147722a9b":
			return "sportsMarker";
		//Beaches & Lakes
		case "637e11aaddc2e1237e0f1d28":
			return "beachMarker";
		//Shopping & Souvenirs
		case "6030e3ee749499e147722a9a":
			return "shoppingMarker";

		//Hotels
		case "6030e3ee749499e147722a8f":
			return "hotelMarker";
		//Hostels
		case "6030e3ee749499e147722a91":
			return "hostelMarker";
		//Campgrounds
		case "61616a9a33b6745519ec19b8":
			return "campgroundsMarker";
		//Villas
		case "637e1204ddc2e1237e0f1d29":
			return "villaMarker";
		//Resorts
		case "637e1280ddc2e1237e0f1d32":
			return "resortMarker";
		//Apartments
		case "6030e3ee749499e147722a90":
			return "apartmentMarker";
		//Bed & Breakfast
		case "6030e3ee749499e147722a93":
			return "bbMarker";

		//Breakfast
		case "637e1293ddc2e1237e0f1d33":
			return "breakfastMarker";
		//Lunch
		case "637e129fddc2e1237e0f1d34":
			return "lunchMarker";
		//Dinner
		case "637e12a7ddc2e1237e0f1d35":
			return "dinnerMarker";
		//Drinks & Nightlife
		case "637e12b1ddc2e1237e0f1d36":
			return "nightlifeMarker";
		//Coffee & Tea
		case "637e12bcddc2e1237e0f1d37":
			return "coffeeMarker";
		//Desserts & Sweets
		case "637e12d6ddc2e1237e0f1d38":
			return "sweetsMarker";

		//general accommodations
		case "accommodation":
			return "sleepMarker";
		//general see & do
		case "activity":
			return resolveActivityMarkerSprite(place) || "seedoMarker";
		//general cuisine
		case "cuisine":
			return "cuisineMarker";

		default:
			return "citymarker";
	}
};
export interface BoardLabel {
	label: string;
	included: boolean;
	// True only when the board is specifically "Room Only" (matched code/keyword, or defaulted with
	// no trusted custom name) — distinct from `!included`, since a custom non-included board name
	// (unrecognized provider label) still has included:false but isn't literally Room Only.
	roomOnly: boolean;
}

// Provider (Nuitee/LiteAPI) short codes — both boardType and boardName often arrive as one of
// these bare codes (e.g. booking.boardName "RO"), which is not a human label on its own.
const CODE_LABELS: Record<string, BoardLabel> = {
	ro: { label: "Room Only", included: false, roomOnly: true },
	bb: { label: "Breakfast Included", included: true, roomOnly: false },
	bi: { label: "Breakfast Included", included: true, roomOnly: false },
	cb: { label: "Breakfast Included", included: true, roomOnly: false },
	hb: { label: "Half Board", included: true, roomOnly: false },
	fb: { label: "Full Board", included: true, roomOnly: false },
	ai: { label: "All Inclusive", included: true, roomOnly: false },
	dn: { label: "Dinner Included", included: true, roomOnly: false },
};

// Board type -> human label + whether meals are included. Shared by RateOptionCard.tsx,
// RateDetailModal.tsx, RoomDetailModal-adjacent rate views and RoomsTab.tsx's BookedRoomCard.
export function getBoardLabel(boardType?: string, boardName?: string): BoardLabel {
	const combined = `${boardType ?? ""} ${boardName ?? ""}`.toLowerCase();
	// Only trust boardName as the display label when it isn't itself a raw provider code —
	// otherwise fall through to the mapped label below (e.g. "RO" -> "Room Only", not "RO").
	const trustedName = boardName && !CODE_LABELS[boardName.trim().toLowerCase()] ? boardName : undefined;

	if (combined.includes("breakfast") || /\b(bb|bi|cb)\b/.test(combined)) return { label: trustedName || "Breakfast Included", included: true, roomOnly: false };
	if (combined.includes("half") || /\bhb\b/.test(combined)) return { label: trustedName || "Half Board", included: true, roomOnly: false };
	if (combined.includes("full") || /\bfb\b/.test(combined)) return { label: trustedName || "Full Board", included: true, roomOnly: false };
	if (combined.includes("all inclusive") || /\bai\b/.test(combined)) return { label: trustedName || "All Inclusive", included: true, roomOnly: false };
	if (combined.includes("dinner") || /\bdn\b/.test(combined)) return { label: trustedName || "Dinner Included", included: true, roomOnly: false };
	if (combined.includes("room only") || /\bro\b/.test(combined)) return { label: trustedName || "Room Only", included: false, roomOnly: true };
	return { label: trustedName ?? "Room Only", included: false, roomOnly: !trustedName };
}
